/**
 * "Are you sure?" in the portal's own dialog, never the browser's.
 *
 * window.confirm and window.alert are grey boxes the portal cannot style or translate,
 * carry the site's address as their title, and freeze the page. This is the same
 * question in a Modal:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ message: t('vehicles.deleteConfirm'), danger: true })) remove();
 *
 * `notice` is the alert: one button, nothing to decide.
 */

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '@/i18n';

import { Modal } from './Modal';

interface Ask {
  title?: string;
  message: string;
  /** The button that goes ahead: "Delete", "Turn off"… */
  confirmLabel?: string;
  /** A red button, for what cannot be undone. */
  danger?: boolean;
  /** Only the one button: an alert, not a question. */
  notice?: boolean;
}

type Confirm = (ask: Ask) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [ask, setAsk] = useState<Ask | null>(null);
  const answer = useRef<((yes: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>(
    (next) =>
      new Promise<boolean>((resolve) => {
        // A question still open is answered "no" by the one replacing it.
        answer.current?.(false);
        answer.current = resolve;
        setAsk(next);
      }),
    [],
  );

  const close = (yes: boolean) => {
    answer.current?.(yes);
    answer.current = null;
    setAsk(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={ask !== null}
        onClose={() => close(false)}
        size="sm"
        title={ask?.title ?? (ask?.notice ? t('common.notice') : t('common.areYouSure'))}
        footer={
          ask?.notice ? (
            <button type="button" className="btn-primary" onClick={() => close(true)}>
              {t('common.ok')}
            </button>
          ) : (
            <>
              <button type="button" className={ask?.danger ? 'btn-danger' : 'btn-primary'} onClick={() => close(true)}>
                {ask?.confirmLabel ?? t('common.confirm')}
              </button>
              <button type="button" className="btn-secondary" onClick={() => close(false)}>
                {t('common.cancel')}
              </button>
            </>
          )
        }
      >
        <p className="text-sm whitespace-pre-line">{ask?.message}</p>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Confirm {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm needs a ConfirmProvider above it.');
  return confirm;
}
